import { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { captureHelpEvent } from "src/config/posthog";
import { loadFAQ } from "./loadFAQ";

interface FAQAccordionProps {
  product: string;
  scrollToSection: string | null;
  onScrollHandled: () => void;
}

const FAQAccordion = ({
  product,
  scrollToSection,
  onScrollHandled,
}: FAQAccordionProps) => {
  const payload = useMemo(() => loadFAQ(product), [product]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!scrollToSection) return;
    const el = sectionRefs.current[scrollToSection];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      onScrollHandled();
    }
  }, [scrollToSection, onScrollHandled, payload]);

  if (!payload || payload.sections.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No FAQ content available for this product yet.
      </Typography>
    );
  }

  const handleExpand =
    (sectionId: string, itemId: string, sectionTitle: string) =>
    (_: unknown, isExpanded: boolean) => {
      const composite = `${sectionId}::${itemId}`;
      setExpanded(isExpanded ? composite : null);
      if (isExpanded) {
        captureHelpEvent("help_faq_item_expanded", {
          product: payload.product,
          item_id: itemId,
          section: sectionTitle,
        });
      }
    };

  return (
    <Stack spacing={3}>
      {payload.sections.map((section) => (
        <Box
          key={section.id}
          ref={(el: HTMLDivElement | null) => {
            sectionRefs.current[section.id] = el;
          }}
          data-testid={`help-faq-section-${section.id}`}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {section.title}
          </Typography>
          {section.items.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No entries in this section yet.
            </Typography>
          ) : (
            section.items.map((item) => {
              const composite = `${section.id}::${item.id}`;
              return (
                <Accordion
                  key={item.id}
                  expanded={expanded === composite}
                  onChange={handleExpand(section.id, item.id, section.title)}
                  disableGutters
                  square={false}
                  sx={{ "&:before": { display: "none" }, mb: 0.5 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    data-testid={`help-faq-item-${section.id}-${item.id}`}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        "& p": { my: 0.5 },
                        "& p:first-of-type": { mt: 0 },
                        "& p:last-of-type": { mb: 0 },
                        "& ul, & ol": { my: 0.5, pl: 3 },
                        "& li": { mb: 0.25 },
                        "& a": { color: "primary.main" },
                      }}
                    >
                      <Markdown remarkPlugins={[remarkGfm]}>
                        {item.answer}
                      </Markdown>
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      ))}
    </Stack>
  );
};

export default FAQAccordion;
